import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { CATEGORY_COLORS, CATEGORY_ICONS, CATEGORY_LABELS } from '../../util/drawingUtils';

const ALL_CATEGORIES = Object.keys(CATEGORY_LABELS);

export default function CategoryPanel({
  allPois,
  hiddenCategories,
  selectedPoiId,
  onToggleCategory,
  onPoiSelect,
  canUpdate,
  canDelete,
}) {
  const [expandedCategories, setExpandedCategories] = useState([]);
  const selectedItemRef = useRef(null);

  // Group POIs by category.
  const poisByCategory = useMemo(() => {
    const map = {};
    (allPois || []).forEach((poi) => {
      const cat = (poi.category && ALL_CATEGORIES.includes(poi.category)) ? poi.category : 'other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(poi);
    });
    return map;
  }, [allPois]);

  const activeCategories = ALL_CATEGORIES.filter((cat) => (poisByCategory[cat] || []).length > 0);

  // When selectedPoiId changes, auto-expand the category that contains it
  // and scroll the item into view.
  useEffect(() => {
    if (!selectedPoiId) return;
    const poi = (allPois || []).find((p) => p && String(p._id) === String(selectedPoiId));
    if (!poi) return;
    const cat = (poi.category && ALL_CATEGORIES.includes(poi.category)) ? poi.category : 'other';
    setExpandedCategories((prev) => prev.includes(cat) ? prev : [...prev, cat]);
  }, [selectedPoiId, allPois]);

  // Scroll selected item into view after render.
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  });

  const toggleExpand = (cat) => {
    setExpandedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  };

  const allVisible = activeCategories.every((cat) => !hiddenCategories.includes(cat));
  const handleToggleAll = () => {
    if (allVisible) {
      activeCategories.forEach((cat) => { if (!hiddenCategories.includes(cat)) onToggleCategory(cat, false); });
    } else {
      activeCategories.forEach((cat) => { if (hiddenCategories.includes(cat)) onToggleCategory(cat, true); });
    }
  };

  return (
    <div className="poi-category-panel">
      <div className="poi-panel-header">
        <span className="poi-panel-title">POIs by Category</span>
        {activeCategories.length > 0 && (
          <Button size="small" onClick={handleToggleAll}>
            {allVisible ? 'Hide All' : 'Show All'}
          </Button>
        )}
      </div>

      {activeCategories.length === 0 && (
        <div className="poi-empty-hint">No POIs yet. Draw a shape to start.</div>
      )}

      {activeCategories.map((cat) => {
        const pois = poisByCategory[cat] || [];
        const isHidden = hiddenCategories.includes(cat);
        const isExpanded = expandedCategories.includes(cat);
        const categoryHasSelected = pois.some((p) => p && String(p._id) === String(selectedPoiId));

        return (
          <div key={cat} className={`poi-category-item${isHidden ? ' poi-category-item-hidden' : ''}`}>
            <div
              className={`poi-layer-row${categoryHasSelected ? ' poi-category-row-active' : ''}`}
              onClick={() => toggleExpand(cat)}
              style={{ cursor: 'pointer' }}
            >
              <span className="poi-cat-icon">{CATEGORY_ICONS[cat] || '📍'}</span>
              <span className="poi-layer-name">{CATEGORY_LABELS[cat] || cat}</span>
              <span className="poi-layer-count">{pois.length}</span>
              <div className="poi-layer-actions" onClick={(e) => e.stopPropagation()}>
                <Tooltip title={isHidden ? 'Show on map' : 'Hide on map'}>
                  <Button
                    size="small"
                    icon={isHidden ? 'eye-invisible' : 'eye'}
                    onClick={() => onToggleCategory(cat, isHidden)}
                  />
                </Tooltip>
              </div>
            </div>

            {isExpanded && pois.length > 0 && (
              <div className="poi-layer-poi-list">
                {pois.map((poi) => {
                  const isSelected = poi && String(poi._id) === String(selectedPoiId);
                  return (
                    <div
                      key={poi._id}
                      ref={isSelected ? selectedItemRef : null}
                      className={`poi-layer-poi-item${isSelected ? ' poi-list-item-selected' : ''}`}
                      onClick={() => onPoiSelect(poi)}
                    >
                      <span className="poi-cat-icon poi-cat-icon-sm">{CATEGORY_ICONS[cat] || '📍'}</span>
                      <span className="poi-layer-poi-name">{poi.name}</span>
                      {isSelected && (
                        <span className="poi-list-item-selected-indicator">✎</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
